import { Box, Typography, useTheme } from '@mui/material';
import { colorTheme } from 'theme';

export type Value = string | number | Record<string | number, string | number>;

export interface AuditInfo {
  row: Row;
}

export interface Row {
  logId: number;
  actionType: string;
  actionDescription: string;
  outcome: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  previousValues: Record<string, Value>;
  currentValues: Record<string, Value>;
  actioner: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const AuditDetails = (props: AuditInfo): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);

  const displayNestedObject = (obj: object, depth = 0): JSX.Element[] => {
    return Object.entries(obj).map(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        return (
          <div key={key}>
            <Typography variant="h5" color={colors.black[500]}>{`${' '.repeat(
              depth * 2,
            )}${key}: {`}</Typography>
            {displayNestedObject(value, depth + 1)}
            <Typography variant="h5" color={colors.black[500]}>{`${' '.repeat(
              depth * 2,
            )}}`}</Typography>
          </div>
        );
      }
      return (
        <Typography key={key} variant="h5" color={colors.black[500]}>
          {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `${' '.repeat(depth * 2)}${key}: ${value}`
          }
        </Typography>
      );
    });
  };

  return (
    <Box
      sx={{
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        boxShadow: 24,
        py: 5,
        px: 5,
        borderRadius: 5,
        maxWidth: '500px',
        wordWrap: 'break-word',
      }}
    >
      <Typography variant="h4">Record Details :</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', py: 1, gap: 1 }}>
        <Typography variant="h5" component="h2">
          Log ID:
        </Typography>
        <Typography variant="h5" component="h2">
          {props.row.logId}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, gap: 1 }}>
        <Typography variant="h5" component="h2">
          Action Type:
        </Typography>
        <Typography variant="h5" component="h2">
          {props.row.actionType}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, gap: 1 }}>
        <Typography variant="h5" component="h2">
          Action Description:
        </Typography>
        <Typography variant="h5" component="h2">
          {props.row.actionDescription}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, gap: 1 }}>
        <Typography variant="h5" component="h2">
          Actioner ID:
        </Typography>
        <Typography variant="h5" component="h2">
          {props.row.createdBy}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, gap: 1 }}>
        <Typography variant="h5" component="h2">
          Actioner Name:
        </Typography>
        <Typography variant="h5" component="h2">
          {props.row.actioner.firstName} {props.row.actioner.lastName}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, gap: 1 }}>
        <Typography variant="h5" component="h2">
          Actioner Email:
        </Typography>
        <Typography variant="h5" component="h2">
          {props.row.actioner.email}
        </Typography>
      </Box>
      {props.row.previousValues !== null && (
        <Box>
          <Typography variant="h5" component="h2">
            Previous Values:
          </Typography>

          <Typography
            sx={{
              backgroundColor: colors.gray[500],
              py: 2,
              px: 5,
              my: 1,
            }}
          >
            {displayNestedObject(props.row.previousValues)}
          </Typography>
        </Box>
      )}

      {props.row.currentValues !== null && (
        <Box>
          <Typography variant="h5" component="h2">
            Current Values:
          </Typography>

          <Typography
            sx={{
              backgroundColor: colors.gray[500],
              py: 2,
              px: 5,
              my: 1,
            }}
          >
            {displayNestedObject(props.row.currentValues)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AuditDetails;
